import {
  CLOUD_LEVELS,
  INVASION,
  OUTPOSTS,
  SHIP_COMBAT,
  OUTPOST_RULES,
  STANCE_IDS,
  type ShipClassId,
  type StanceId,
} from '@sd/content';
import type { MatchSetup } from '@sd/bots';
import { t, type Locale } from '@sd/i18n';
import {
  PIRATES,
  liveCloudAt,
  commandBlocker,
  findPath,
  hexDistance,
  isExplored,
  fleetStance,
  hexKey,
  hullShare,
  isArmed,
  leadShip,
  outpostLimit,
  outpostSites,
  outpostYield,
  seenFleets,
  siteAt,
  toLocal,
  spaceportLevel,
  visibleHexes,
  visionInput,
  type Command,
  type FleetState,
  type GameState,
  type Hex,
} from '@sd/sim';
import { clock, fleetName, num, outpostName, playerName, shortOf } from '../ui/format';
import { reasonText } from '../ui/planetModel';
import { scanProgress } from './progress';

/**
 * Words for what the player points at on the map (#55): the tooltip and the
 * selection card. Pure: state in, text and commands out, so it is tested
 * without a browser.
 */
export interface Viewer {
  readonly me: string;
  readonly locale: Locale;
  readonly setup: MatchSetup;
}

export interface HexInfo {
  readonly title: string;
  readonly lines: readonly string[];
}

const ownerName = (v: Viewer, owner: string) =>
  owner === PIRATES ? t(v.locale, 'find.pirates.name') : playerName(v.locale, v.setup, owner);

/** «2 × Фрегат, Корвет». */
export function shipSummary(locale: Locale, classes: readonly ShipClassId[]): string {
  const counts = new Map<ShipClassId, number>();
  for (const c of classes) counts.set(c, (counts.get(c) ?? 0) + 1);
  return [...counts]
    .map(([c, n]) =>
      n > 1 ? `${n} × ${t(locale, `ship.${c}.name`)}` : t(locale, `ship.${c}.name`),
    )
    .join(', ');
}

/** What a hex holds, as the player may see it. */
export function describeHex(state: GameState, hex: Hex, v: Viewer): HexInfo {
  const { locale } = v;
  const key = hexKey(hex);
  const system = state.system;
  const planet = system.planets.find((p) => hexKey(p.hex) === key);
  const lagrange = system.lagrangePoints.find((l) => hexKey(l.hex) === key);
  const cloud = liveCloudAt(system, state.exploration.findsLeft, hex);
  // Never seen (#97): nothing is known there yet but the star.
  const isStar = system.starHexes.some((h) => hexKey(h) === key);
  if (!isStar && !isExplored(state, v.me, hex)) {
    return { title: t(locale, 'ui.map.unexplored'), lines: [t(locale, 'ui.map.unexploredHint')] };
  }

  let title: string;
  if (planet?.homeOf) title = t(locale, 'ui.map.capital', { name: ownerName(v, planet.homeOf) });
  else if (planet) title = t(locale, `planet.${planet.kind}.name`);
  else if (lagrange) title = t(locale, 'ui.map.lagrange', { side: lagrange.side });
  else if (system.starHexes.some((h) => hexKey(h) === key)) title = t(locale, 'ui.map.star');
  else if (system.coronaHexes.some((h) => hexKey(h) === key)) title = t(locale, 'zone.corona');
  else if (cloud) title = t(locale, 'map.dustCloud.level', { level: cloud.level });
  else title = t(locale, 'ui.map.space');

  const lines: string[] = [];
  // What a Lagrange point is for (#125: players did not know how to use one).
  if (lagrange && !state.outposts.some((o) => hexKey(o.hex) === key)) {
    lines.push(t(locale, 'ui.map.lagrangeHint'));
  }
  if (cloud && title !== t(locale, 'map.dustCloud.level', { level: cloud.level })) {
    lines.push(t(locale, 'map.dustCloud.level', { level: cloud.level }));
  }
  for (const o of state.outposts.filter((x) => hexKey(x.hex) === key)) {
    const name = outpostName(locale, o);
    let line = t(locale, 'ui.map.outpostOf', { outpost: name, owner: ownerName(v, o.owner) });
    if (o.downUntil !== undefined && o.downUntil > state.tick) {
      line += ` · ${t(locale, 'ui.map.outpostDown', { time: clock(o.downUntil) })}`;
    }
    lines.push(line);
  }
  const input = visionInput(state);
  const visible = visibleHexes(input, v.me);
  for (const f of seenFleets(input, v.me, visible).filter((x) => hexKey(x.hex) === key)) {
    const ships = shipSummary(
      locale,
      f.ships.map((s) => s.cls),
    );
    lines.push(`${t(locale, 'ui.map.fleetOf', { owner: ownerName(v, f.owner) })} — ${ships}`);
  }
  const debris = state.debris.find((d) => hexKey(d.hex) === key);
  if (debris) lines.push(t(locale, 'ui.map.debris', { metals: num(locale, debris.metals) }));
  // Fog of war (#33): the map is known, the fleets out there are not.
  if (!visible.has(key)) lines.push(t(locale, 'ui.map.fogged'));
  return { title, lines };
}

export interface CardAction {
  readonly id: string;
  readonly label: string;
  readonly command: Command | null;
  readonly primary?: boolean;
  /** Why the order cannot be given now, when it cannot. */
  readonly reason?: string;
  /** What pressing it costs, shown by the button (#150). */
  readonly price?: string;
}

export interface FleetCard {
  readonly title: string;
  readonly meta: string;
  /** The class it goes by, for its icon (#166). */
  readonly lead?: ShipClassId;
  /** Hull left, per cent. */
  readonly hull?: number;
  /** What it does now and its stance, in a few words (#166: the compact card). */
  readonly status?: string;
  /** A war about to start: the one line the compact card keeps. */
  readonly alert?: string;
  readonly actions: readonly CardAction[];
  /** A scan under way: the current find's share and how many finds of the cloud (#96). */
  readonly progress?: { readonly value: number; readonly label: string };
  /** What this fleet is for and what to do next, in plain words (#96). */
  readonly hints: readonly string[];
}

const nextStance = (s: StanceId): StanceId =>
  STANCE_IDS[(STANCE_IDS.indexOf(s) + 1) % STANCE_IDS.length]!;

/** The selection card of one of the player's own fleets, with its orders. */
/** The nearest of `hexes` the fleet can fly to, and how far; null when none. */
function nearest(state: GameState, fleet: FleetState, hexes: readonly Hex[]) {
  // The pathfinder runs on the closest few only.
  const close = [...hexes]
    .sort(
      (a, b) =>
        hexDistance(fleet.hex, a) - hexDistance(fleet.hex, b) || hexKey(a).localeCompare(hexKey(b)),
    )
    .slice(0, 4);
  let best: { to: Hex; hexes: number } | null = null;
  for (const to of close) {
    const path = findPath(state.system, fleet.hex, to);
    if (path && path.length > 0 && (!best || path.length < best.hexes))
      best = { to, hexes: path.length };
  }
  return best;
}

/** «К облаку» for an explorer out of one, «К месту добычи» for a constructor (#162). */
function quickDestinations(
  state: GameState,
  fleet: FleetState,
  v: Viewer,
  explorer: boolean,
  constructor: boolean,
): CardAction[] {
  const { locale, me } = v;
  const base = { playerId: me, fleetId: fleet.id } as const;
  const out: CardAction[] = [];
  const techs = state.players.find((p) => p.id === me)?.techs ?? [];
  if (explorer) {
    const left = state.exploration.findsLeft;
    const clouds = state.system.clouds.filter((c) => {
      const tech = CLOUD_LEVELS[c.level].requiresTech;
      return (left[c.id] ?? 0) > 0 && (!tech || techs.includes(tech));
    });
    const go = nearest(
      state,
      fleet,
      clouds.flatMap((c) => c.hexes),
    );
    if (go) {
      out.push({
        id: 'toCloud',
        label: t(locale, 'ui.order.toCloud', { n: go.hexes }),
        command: { type: 'moveFleet', ...base, to: go.to },
      });
    }
  }
  const taken = new Set(state.outposts.map((o) => o.id));
  const free = constructor
    ? outpostSites(state.system, state.exploration).filter((s) => !taken.has(s.id))
    : [];
  if (constructor && !siteAt(free, fleet.hex)) {
    const go = nearest(
      state,
      fleet,
      free.map((s) => s.hex),
    );
    if (go) {
      out.push({
        id: 'toSite',
        label: t(locale, 'ui.order.toSite', { n: go.hexes }),
        command: { type: 'moveFleet', ...base, to: go.to },
      });
    }
  }
  return out;
}

export function fleetCard(state: GameState, fleet: FleetState, v: Viewer): FleetCard {
  const { locale, me } = v;
  const stance = fleetStance(fleet);
  const hull = Math.round(hullShare(fleet) * 100);
  const scan = scanProgress(state, fleet.id);
  const motion = fleet.scan
    ? t(locale, 'ui.map.scanning')
    : fleet.path.length > 0
      ? t(locale, 'ui.map.moving', { hexes: fleet.path.length })
      : t(locale, 'ui.map.holding');
  const meta = [
    shipSummary(
      locale,
      fleet.ships.map((s) => s.cls),
    ),
    motion,
    // Unarmed ships have no stance (#154).
    ...(isArmed(fleet)
      ? [t(locale, 'ui.map.stance', { stance: t(locale, `stance.${stance}.name`).toLowerCase() })]
      : []),
    t(locale, 'ui.map.hull', { pct: hull }),
  ].join(' · ');

  const capital = state.system.planets.find((p) => p.homeOf === me)?.hex;
  const atHome = capital !== undefined && hexKey(capital) === hexKey(fleet.hex);
  const cloud = liveCloudAt(state.system, state.exploration.findsLeft, fleet.hex);
  const inCloud = cloud !== undefined;
  const hasTransport = fleet.ships.some((s) => s.cls === 'transport');
  const hasExplorer = fleet.ships.some((s) => s.cls === 'explorer');
  const base = { playerId: me, fleetId: fleet.id } as const;
  // Why a scan cannot start here: no explorer, the cloud is exhausted, a scanner is missing.
  const scanCommand = { type: 'scan', ...base } as const;
  const scanBlocker = inCloud && !fleet.scan ? commandBlocker(state, scanCommand) : null;
  const scanTech = cloud ? (CLOUD_LEVELS[cloud.level].requiresTech ?? undefined) : undefined;
  // Extraction (#103): why it cannot start here, said on the button rather than after a click.
  const outpostCommand = { type: 'buildOutpost', ...base } as const;
  const outpostBlocker =
    hasTransport && fleet.path.length === 0 ? commandBlocker(state, outpostCommand) : null;
  // What extraction here costs and gives (#109): the price, and what is missing of it.
  const player = state.players.find((p) => p.id === me);
  const site = hasTransport
    ? siteAt(outpostSites(state.system, state.exploration), fleet.hex)
    : undefined;
  const price =
    site && player
      ? {
          cash: toLocal(OUTPOSTS[site.kind].cost.cash, player.currency.rate),
          alloys: OUTPOSTS[site.kind].cost.alloys,
        }
      : null;
  const short = price && player ? shortOf(locale, player, price.cash, price.alloys) : null;
  const outpostReason =
    outpostBlocker && short && /not enough/.test(outpostBlocker)
      ? short
      : outpostBlocker
        ? reasonText(locale, outpostBlocker, undefined, 'fleet')
        : null;
  const actions: CardAction[] = [
    { id: 'move', label: `${t(locale, 'ui.order.move')}`, command: null },
    {
      id: 'stop',
      label: t(locale, 'ui.order.stop'),
      command: fleet.path.length > 0 ? { type: 'moveFleet', ...base, to: fleet.hex } : null,
    },
    {
      id: 'scan',
      label: t(locale, 'ui.order.scan'),
      command: inCloud && !fleet.scan && !scanBlocker ? scanCommand : null,
      primary: inCloud && !fleet.scan && !scanBlocker,
      ...(scanBlocker ? { reason: reasonText(locale, scanBlocker, scanTech) } : {}),
    },
    // Only a constructor builds extraction (#150): the order shows with one in the fleet.
    ...(hasTransport
      ? [
          {
            id: 'outpost',
            label: t(locale, 'ui.order.outpost'),
            command: !outpostBlocker ? outpostCommand : null,
            primary: !outpostBlocker,
            ...(outpostReason ? { reason: outpostReason } : {}),
            ...(price && !outpostReason
              ? {
                  price: t(locale, 'ui.map.price', {
                    cash: num(locale, price.cash),
                    alloys: price.alloys,
                  }),
                }
              : {}),
          },
        ]
      : []),
    // Unarmed ships have no stance (#154).
    ...(isArmed(fleet)
      ? [
          {
            id: 'stance',
            label: `${t(locale, 'ui.order.stance')} → ${t(locale, `stance.${nextStance(stance)}.name`).toLowerCase()}`,
            command: { type: 'setStance' as const, ...base, stance: nextStance(stance) },
          },
        ]
      : []),
    {
      id: 'home',
      label: t(locale, 'ui.order.home'),
      command: capital && !atHome ? { type: 'moveFleet', ...base, to: capital } : null,
    },
    // One click to where the ship is for (#162): no aiming at a small hex with a touchpad.
    ...quickDestinations(state, fleet, v, hasExplorer && !inCloud, hasTransport),
  ];
  // War orders on the map card too (#125: they were only on the fleet tab): a raid for
  // armed fleets, a landing once it is learnt — with why not, while at war.
  const war = state.wars.some((w) => w.a === me || w.b === me);
  if (war && fleet.ships.some((s) => SHIP_COMBAT[s.cls].damage > 0)) {
    const raid = { type: 'raid', ...base } as const;
    const why = state.raids.some((r) => r.fleetId === fleet.id)
      ? 'already raiding'
      : commandBlocker(state, raid);
    actions.push({
      id: 'raid',
      label: t(locale, 'fleet.order.raid'),
      command: why ? null : raid,
      primary: !why,
      ...(why ? { reason: reasonText(locale, why, undefined, 'fleet') } : {}),
    });
  }
  if (war && player?.techs.includes(INVASION.requiresTech)) {
    const invade = { type: 'invade', ...base } as const;
    const why = commandBlocker(state, invade);
    actions.push({
      id: 'invade',
      label: t(locale, 'fleet.order.invade'),
      command: why ? null : invade,
      primary: !why,
      ...(why ? { reason: reasonText(locale, why, undefined, 'fleet') } : {}),
    });
  }
  const hints: string[] = [];
  const coming = state.pendingWars.find((w) => w.by === me || w.target === me);
  const alert = coming
    ? t(locale, 'ui.map.warSoon', { time: clock(Math.max(0, coming.startsAt - state.tick)) })
    : undefined;
  if (alert) hints.push(alert);
  if (hasExplorer && !hasTransport) {
    // The explorer's job and what it is not for: outposts need a transport (#96).
    hints.push(
      t(
        locale,
        fleet.scan
          ? 'ui.map.explorerScanning'
          : inCloud
            ? 'ui.map.explorerHere'
            : 'ui.map.explorerGo',
      ),
    );
    hints.push(t(locale, 'ui.map.outpostsNeedTransport'));
  }
  if (hasTransport) {
    if (site && price && player) {
      hints.push(
        t(locale, 'fleet.outposts.here', {
          name: outpostName(locale, site),
          n: num(locale, outpostYield(player, site), 1),
          good: t(locale, `resource.${site.good}`).toLowerCase(),
          cash: num(locale, price.cash),
          alloys: price.alloys,
          upkeep: OUTPOSTS[site.kind].upkeep,
        }),
      );
    }
    // How many extraction sites the spaceport allows, and what the next level gives.
    if (player) {
      const level = spaceportLevel(player.planet);
      const held = state.outposts.filter((o) => o.owner === me).length;
      hints.push(t(locale, 'fleet.outposts.limit', { held, max: outpostLimit(level), level }));
      if (level < OUTPOST_RULES.limitBySpaceportLevel.length) {
        hints.push(
          t(locale, 'fleet.outposts.next', { level: level + 1, max: outpostLimit(level + 1) }),
        );
      }
    }
  }
  if (scanBlocker) {
    hints.unshift(
      t(locale, 'ui.map.scanBlocked', { reason: reasonText(locale, scanBlocker, scanTech) }),
    );
  }
  return {
    title: fleetName(locale, fleet),
    meta,
    lead: leadShip(fleet).cls,
    hull,
    status: [
      ...(fleet.ships.length > 1
        ? [
            shipSummary(
              locale,
              fleet.ships.map((s) => s.cls),
            ),
          ]
        : []),
      motion,
      ...(isArmed(fleet) ? [t(locale, `stance.${stance}.name`).toLowerCase()] : []),
    ].join(' · '),
    ...(alert ? { alert } : {}),
    // A transport-free fleet never founds outposts: the button would only confuse.
    actions: actions.filter((a) => a.id !== 'outpost' || hasTransport),
    ...(scan
      ? {
          progress: {
            value: scan.value,
            label: t(locale, 'ui.progress.scan', { n: scan.done + 1, total: scan.total }),
          },
        }
      : {}),
    hints,
  };
}
